import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Settings as SettingsIcon, ArrowLeft, Save, Sparkles, AlertTriangle,
  Plus, Trash2, Pause, Play,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'

const TIPOS_LIBERAVEIS = [
  { key: 'pergunta_pre_venda', label: 'Pré-venda simples (ficha técnica)' },
  { key: 'status_envio', label: 'Status de envio (rastreio)' },
  { key: 'politica_troca', label: 'Política de troca/devolução' },
  { key: 'disponibilidade_estoque', label: 'Disponibilidade de estoque' },
  { key: 'agradecimento', label: 'Agradecimento / confirmação' },
  { key: 'reembolso_simples', label: 'Reembolso parcial (com aprovação)' },
  { key: 'defeito_produto', label: 'Defeito de produto (NUNCA recomendado)' },
]

export default function SACConfig() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<any>({
    queryKey: ['sac-config'],
    queryFn: async () => (await api.get('/sac/configuracao')).data,
  })

  const [form, setForm] = useState<any>({
    ia_ativada: true,
    ia_resposta_apos_min: 30,
    sla_resposta_humano_horas: 4,
    notificar_imediato: true,
    persona_nome: '',
    persona_descricao: '',
    tom_casualidade: 5,
    politica_empresa: '',
    nunca_prometer: '',
    exemplos_resposta: [],
    tipos_liberados_ia: [],
  })

  useEffect(() => {
    if (data) {
      setForm({
        ia_ativada: data.ia_ativada,
        ia_resposta_apos_min: data.ia_resposta_apos_min,
        sla_resposta_humano_horas: data.sla_resposta_humano_horas,
        notificar_imediato: data.notificar_imediato,
        persona_nome: data.persona_nome || '',
        persona_descricao: data.persona_descricao || '',
        tom_casualidade: data.tom_casualidade,
        politica_empresa: data.politica_empresa || '',
        nunca_prometer: data.nunca_prometer || '',
        exemplos_resposta: data.exemplos_resposta || [],
        tipos_liberados_ia: data.tipos_liberados_ia || [],
      })
    }
  }, [data])

  const salvar = useMutation({
    mutationFn: async () => (await api.put('/sac/configuracao', form)).data,
    onSuccess: () => {
      toast.success('Configuração salva')
      qc.invalidateQueries({ queryKey: ['sac-config'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const pausar = useMutation({
    mutationFn: async (horas: number) => {
      const ate = new Date(Date.now() + horas * 3600 * 1000).toISOString()
      return (await api.put('/sac/configuracao', { pausar_ia_ate: ate })).data
    },
    onSuccess: () => {
      toast.success('Sofia pausada')
      qc.invalidateQueries({ queryKey: ['sac-config'] })
    },
  })

  const retomar = useMutation({
    mutationFn: async () => (await api.put('/sac/configuracao', { pausar_ia_ate: null })).data,
    onSuccess: () => {
      toast.success('Sofia retomada')
      qc.invalidateQueries({ queryKey: ['sac-config'] })
    },
  })

  function toggleTipo(key: string) {
    setForm((f: any) => {
      const lista = new Set(f.tipos_liberados_ia || [])
      if (lista.has(key)) lista.delete(key)
      else lista.add(key)
      return { ...f, tipos_liberados_ia: Array.from(lista) }
    })
  }

  function addExemplo() {
    setForm((f: any) => ({
      ...f,
      exemplos_resposta: [...(f.exemplos_resposta || []), { cliente: '', voce: '' }],
    }))
  }

  function delExemplo(i: number) {
    setForm((f: any) => ({
      ...f,
      exemplos_resposta: (f.exemplos_resposta || []).filter((_: any, idx: number) => idx !== i),
    }))
  }

  function updExemplo(i: number, campo: string, valor: string) {
    setForm((f: any) => ({
      ...f,
      exemplos_resposta: (f.exemplos_resposta || []).map((x: any, idx: number) =>
        idx === i ? { ...x, [campo]: valor } : x
      ),
    }))
  }

  if (isLoading) return <div className="p-12 text-center text-ink-400">Carregando...</div>

  const pausadaAte = data?.pausar_ia_ate ? new Date(data.pausar_ia_ate) : null
  const ativaPausa = pausadaAte && pausadaAte > new Date()

  return (
    <div className="p-4 lg:p-6 max-w-3xl mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/sac" className="p-1.5 rounded hover:bg-ink-100">
          <ArrowLeft size={16} />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <SettingsIcon size={20} className="text-violet-600" /> Configuração do SAC
          </h1>
          <p className="text-xs text-ink-500">Persona da Sofia, tom de voz, regras de resposta automática</p>
        </div>
      </div>

      {/* Bloco pausa */}
      <div className={`border rounded-lg p-3 ${ativaPausa ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'}`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-semibold text-sm flex items-center gap-2">
              {ativaPausa
                ? <><Pause size={14} className="text-amber-700" /> Sofia pausada até {pausadaAte?.toLocaleString('pt-BR')}</>
                : <><Play size={14} className="text-emerald-700" /> Sofia ativa</>}
            </div>
            <div className="text-[11px] text-ink-600 mt-0.5">
              {ativaPausa
                ? 'Mensagens recebidas neste período só serão respondidas manualmente.'
                : 'Use o botão pausa em caso de viagem, crise ou ajuste de processo.'}
            </div>
          </div>
          {ativaPausa ? (
            <button onClick={() => retomar.mutate()} className="btn-primary text-xs">
              Retomar Sofia agora
            </button>
          ) : (
            <div className="flex gap-1">
              {[2, 8, 24, 72].map(h => (
                <button key={h} onClick={() => pausar.mutate(h)} className="text-xs px-2 py-1 rounded border border-amber-300 hover:bg-amber-100">
                  Pausar {h}h
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Tempos */}
      <Bloco titulo="Tempos de resposta">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Campo label="Sofia responde após (min)"
            help="Minutos sem resposta humana antes da Sofia enviar mensagem automática">
            <input type="number" min={5} max={1440} className="input" value={form.ia_resposta_apos_min}
              onChange={e => setForm({ ...form, ia_resposta_apos_min: Number(e.target.value) })} />
          </Campo>
          <Campo label="SLA do atendente (horas)"
            help="Prazo máximo pra resposta humana (depois disso: alerta vermelho)">
            <input type="number" min={1} max={48} className="input" value={form.sla_resposta_humano_horas}
              onChange={e => setForm({ ...form, sla_resposta_humano_horas: Number(e.target.value) })} />
          </Campo>
        </div>
        <label className="flex items-center gap-2 mt-3 text-sm">
          <input type="checkbox" checked={form.notificar_imediato}
            onChange={e => setForm({ ...form, notificar_imediato: e.target.checked })} />
          Notificar atendente no momento que mensagem chega
        </label>
        <label className="flex items-center gap-2 mt-2 text-sm">
          <input type="checkbox" checked={form.ia_ativada}
            onChange={e => setForm({ ...form, ia_ativada: e.target.checked })} />
          Sofia habilitada (master switch)
        </label>
      </Bloco>

      {/* Persona */}
      <Bloco titulo="Persona da Sofia" icone={<Sparkles size={14} className="text-violet-600" />}>
        <Campo label="Nome da atendente"
          help="Como a Sofia se identifica nas mensagens (sem sobrenome — fica natural)">
          <input className="input" value={form.persona_nome}
            placeholder="Ex: Marina, Lucas, Bruna..."
            onChange={e => setForm({ ...form, persona_nome: e.target.value })} />
        </Campo>

        <Campo label="Como ela é"
          help="Descreva o jeito da atendente — a Sofia usa isso pra responder no tom certo">
          <textarea className="input min-h-[100px]" value={form.persona_descricao}
            placeholder="Ex: Marina, 28 anos, atendente da IBK Calçados. Tem jeito calmo e prestativa, usa gírias leves do Sul, trata o cliente pelo primeiro nome. Sempre empática."
            onChange={e => setForm({ ...form, persona_descricao: e.target.value })} />
        </Campo>

        <Campo label="Tom da conversa">
          <div className="flex items-center gap-3">
            <span className="text-xs text-ink-500">Formal</span>
            <input type="range" min={0} max={10} className="flex-1"
              value={form.tom_casualidade}
              onChange={e => setForm({ ...form, tom_casualidade: Number(e.target.value) })} />
            <span className="text-xs text-ink-500">Casual</span>
            <span className="text-xs font-bold w-10 text-right">{form.tom_casualidade}/10</span>
          </div>
          <div className="text-[11px] text-ink-500 mt-1">
            {form.tom_casualidade <= 3 && 'Formal — "Prezado cliente, agradecemos seu contato..."'}
            {form.tom_casualidade > 3 && form.tom_casualidade <= 6 && 'Equilibrado — "Olá! Recebi sua mensagem..."'}
            {form.tom_casualidade > 6 && 'Casual — "oi {nome}! deixa eu ver aqui... 😊"'}
          </div>
        </Campo>
      </Bloco>

      {/* Política */}
      <Bloco titulo="Política da empresa (base de conhecimento)">
        <Campo label="O que a Sofia pode prometer"
          help="Diga as regras: prazo de troca, frete reverso, garantia, política de devolução. A Sofia usa como referência.">
          <textarea className="input min-h-[120px]" value={form.politica_empresa}
            placeholder={"Ex:\n- Trocas em 30 dias com NF\n- Frete reverso por nossa conta se defeito\n- Numeração: 33 a 45\n- Pedido pago confirma em até 24h"}
            onChange={e => setForm({ ...form, politica_empresa: e.target.value })} />
        </Campo>

        <Campo label="O que a Sofia NUNCA deve prometer"
          help="Tudo que precisa de avaliação humana ou aprovação do gestor">
          <textarea className="input min-h-[80px]" value={form.nunca_prometer}
            placeholder={"Ex:\n- Prazo de entrega específico (varia por região)\n- Desconto sem aprovação\n- Garantia além de 30 dias\n- Reembolso total imediato"}
            onChange={e => setForm({ ...form, nunca_prometer: e.target.value })} />
        </Campo>
      </Bloco>

      {/* Tipos liberados */}
      <Bloco titulo="Quando a Sofia pode responder sozinha">
        <p className="text-xs text-ink-600 mb-2">
          Marque os tipos de mensagem onde a Sofia tem autonomia. Tudo NÃO marcado fica esperando você responder
          manualmente, mesmo que passe do prazo.
        </p>
        <div className="space-y-1.5">
          {TIPOS_LIBERAVEIS.map(t => (
            <label key={t.key} className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
              (form.tipos_liberados_ia || []).includes(t.key)
                ? 'bg-emerald-50 border-emerald-300'
                : 'border-ink-200 hover:bg-ink-50'
            }`}>
              <input type="checkbox" checked={(form.tipos_liberados_ia || []).includes(t.key)}
                onChange={() => toggleTipo(t.key)} />
              <span className="text-xs flex-1">{t.label}</span>
              {t.key === 'defeito_produto' && (
                <span className="text-[9px] uppercase font-bold text-red-600 flex items-center gap-1">
                  <AlertTriangle size={10} /> Risco
                </span>
              )}
            </label>
          ))}
        </div>
      </Bloco>

      {/* Exemplos */}
      <Bloco titulo="Exemplos de boas respostas">
        <p className="text-xs text-ink-600 mb-2">
          Cole aqui 3 a 5 exemplos seus de resposta perfeita. A Sofia usa pra calibrar o tom.
        </p>
        <div className="space-y-2">
          {(form.exemplos_resposta || []).map((ex: any, i: number) => (
            <div key={i} className="border border-ink-200 rounded p-2 space-y-1">
              <textarea className="input text-xs min-h-[40px]"
                placeholder="Mensagem do cliente"
                value={ex.cliente || ''}
                onChange={e => updExemplo(i, 'cliente', e.target.value)} />
              <textarea className="input text-xs min-h-[40px] bg-violet-50"
                placeholder="Sua resposta ideal"
                value={ex.voce || ''}
                onChange={e => updExemplo(i, 'voce', e.target.value)} />
              <button onClick={() => delExemplo(i)} className="text-xs text-red-600 hover:underline flex items-center gap-1">
                <Trash2 size={10} /> Remover
              </button>
            </div>
          ))}
          <button onClick={addExemplo} className="text-xs px-3 py-1.5 rounded border border-dashed border-ink-300 hover:bg-ink-50 flex items-center gap-1">
            <Plus size={11} /> Adicionar exemplo
          </button>
        </div>
      </Bloco>

      {/* Salvar */}
      <div className="sticky bottom-0 bg-white border-t border-ink-200 p-3 -mx-4 lg:-mx-6 flex justify-end gap-2">
        <button onClick={() => salvar.mutate()} disabled={salvar.isPending}
          className="btn-primary flex items-center gap-2">
          <Save size={14} /> {salvar.isPending ? 'Salvando...' : 'Salvar configurações'}
        </button>
      </div>
    </div>
  )
}

function Bloco({ titulo, icone, children }: any) {
  return (
    <div className="bg-white border border-ink-200 rounded-lg p-4">
      <h2 className="font-semibold text-sm mb-3 flex items-center gap-2 text-ink-900">
        {icone} {titulo}
      </h2>
      {children}
    </div>
  )
}

function Campo({ label, help, children }: any) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-semibold text-ink-700 mb-1">{label}</label>
      {children}
      {help && <div className="text-[10px] text-ink-500 mt-1">{help}</div>}
    </div>
  )
}
